import React, { ReactNode, useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, ButtonVariant } from '../../../common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { TextInput } from '../../../common/TextInput'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { FormControl } from '../../../common/FormControl'
import { societyDocumentFolderCreateSchema } from '../../../../forms/schemas/society_document_folder_create'
import { IDocumentModel } from '../../../../interfaces/models/documents.interfaces'
import { Image } from '../../../common/Image'
import {
  DropZone,
  DropZoneFile,
  DropZoneVariant,
} from '../../../common/DropZone'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { TextArea } from '../../../common/TextArea'
import { FormatTextPopover } from '../../../feed/CreateEditPostModal/CreateEditPostModalContent/FormatTextPopover'
import { createBinderThumbnail } from '../../../../api/binder'
import { NBinder } from '../../../../interfaces/services/binder.interfaces'
import { IBinderCreate } from '../../../../interfaces/models/binder.interfaces'
import { i18nSocietyType } from '../../../../i18n'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { theme } from '../../../../theme/theme'
import { SelectDropdown } from '../../../common/SelectDropdown'
import { NOTIFICATION_SETTINGS_VALUE } from '../../../../helpers/notification_settings_options'

interface CreateUpdateFolderFormProps {
  societyId: string
  onError: (error: unknown) => void
  onSubmit: (
    data: InferType<typeof societyDocumentFolderCreateSchema>
  ) => Promise<void>
  onClose: () => void
  loading: boolean
  folder?: IDocumentModel
  published?: boolean
  optionSettings: {
    value: 'none' | 'notification'
    label: string
  }[]
}

export const CreateUpdateBinderFolderForm = ({
  folder,
  loading,
  onError,
  onSubmit,
  onClose,
  societyId,
  published,
  optionSettings,
}: CreateUpdateFolderFormProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()
  const { setToastNotification } = useToastNotifications()
  const { society } = useCurrentSociety()

  const updateMode = !!folder

  const getDefaultValues = useCallback((): InferType<
    typeof societyDocumentFolderCreateSchema
  > => {
    return {
      name: updateMode ? folder?.name || '' : '',
      description: updateMode ? folder?.description || '' : '',
      thumbnail: updateMode ? folder?.thumbnail || '' : '',
      notificationSettings: NOTIFICATION_SETTINGS_VALUE.NONE,
    }
  }, [updateMode, folder])

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyDocumentFolderCreateSchema),
    defaultValues: getDefaultValues(),
  })

  const { mutate: mutateCreateThumbnail, isPending: isUploadingPicture } =
    useMutation({
      mutationFn: async (data: NBinder.NCreateThumbnail.IRequestBody) => {
        const document = (await createBinderThumbnail(societyId, data)).data
          .data
        if (!document) {
          throw new Error('no docId returned by createDocument')
        }
        return document
      },
      onSuccess: (document) => {
        setValue('thumbnail', document.thumbnail, {
          shouldValidate: true,
          shouldDirty: true,
        })
      },
      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate('flashMessage.somethingWentWrongError')
        )
      },
    })
  const uploadImage = async (file: DropZoneFile): Promise<void> => {
    const data = {
      data: file.base64,
      type: file.name.split('.').pop() as IBinderCreate['type'],
      filename: file.name,
      name: file.name,
    } satisfies NBinder.NCreate.IRequestBody
    mutateCreateThumbnail(data)
  }

  return (
    <>
      <Controller
        control={control}
        render={({ field: { name, value } }) => (
          <FormControl
            label={translate('createUpdateFolderForm.labels.thumbnail')}
            error={errors.thumbnail && getErrorMessage(errors.thumbnail)}
            name={name}
          >
            {value ? (
              <div className="flex justify-center md:h-80">
                <Image
                  url={value}
                  onDeleteClick={() =>
                    setValue('thumbnail', '', {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                  objectFit="object-contain"
                />
              </div>
            ) : (
              <div>
                <DropZone
                  loading={isUploadingPicture}
                  onUpload={(status, file) => {
                    if (status === 'accepted') {
                      uploadImage(file)
                    } else {
                      setToastNotification(
                        ToastType.DANGER,
                        translate('flashMessage.somethingWentWrongError')
                      )
                    }
                  }}
                  variant={DropZoneVariant.MEDIA}
                />
              </div>
            )}
          </FormControl>
        )}
        name="thumbnail"
      />
      <Controller
        control={control}
        name="name"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('createUpdateResourceModal.labels.title')}
            error={errors.name && getErrorMessage(errors.name)}
            name={name}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.name}
            />
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name="description"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('createUpdateResourceModal.labels.description')}
            name={name}
            error={errors.description && getErrorMessage(errors.description)}
          >
            <TextArea
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.description}
            />
          </FormControl>
        )}
      />
      {!updateMode && published && (
        <Controller
          control={control}
          name="notificationSettings"
          render={({ field: { onChange, value, name } }) => (
            <FormControl
              label={translate('common.notificationSettings.label', {
                context: i18nSocietyType(society?.societyTypeDisplayName),
              })}
              labelStyle={theme.textVariants.base}
              name={name}
              error={
                errors.notificationSettings &&
                getErrorMessage(errors.notificationSettings)
              }
            >
              <SelectDropdown
                options={optionSettings}
                error={!!errors.notificationSettings}
                value={optionSettings.find((option) => option.value === value)}
                onChange={(option) => onChange(option?.value)}
              />
            </FormControl>
          )}
        />
      )}

      <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-4">
        <FormatTextPopover />
        <Button label={translate('common.actions.cancel')} onClick={onClose} />
        <Button
          loading={loading}
          variant={ButtonVariant.PRIMARY}
          disabled={!isValid || loading}
          label={translate(
            updateMode ? 'common.actions.save' : 'common.actions.create'
          )}
          type="submit"
          onClick={handleSubmit(onSubmit, onError)}
        />
      </div>
    </>
  )
}
